// Top-bar "New Quote" flow. Staff types name / postcode / email / phone;
// the modal debounces the search and shows ranked matches with a badge
// per matched field. Picking a match creates a blank B_TVISIT on the
// existing account and hands the new vid to the caller (App.tsx), which
// opens the QuoteEditor. If no match (or staff deliberately creates
// anyway), the same form body is used to create a brand-new account +
// contact + location + blank visit in one call chain.

import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  AccountSearchMatchField,
  AccountSearchResult,
} from "@breeze/shared";
import {
  createAccountVisit,
  createNewAccount,
  searchAccounts,
} from "../api.js";
import { PhoneLink } from "./PhoneLink.js";

type Props = {
  open: boolean;
  onClose: () => void;
  // App.tsx opens the QuoteEditor for this vid after a pick or create.
  onOpenQuote: (newVid: number) => void;
  // Click "View account" on a result. App decides whether to open the
  // regular Slideout (active card and present in the visible board set)
  // or the AccountSlideout preview (no card, archived card, or a card
  // filtered out of the current board view). We pass both aid and the
  // result's cardId so App can route correctly.
  onViewAccount: (aid: number, cardId: number | null) => void;
};

type Form = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  postcode: string;
};

const EMPTY_FORM: Form = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  postcode: "",
};

export function NewQuoteSearchModal({
  open,
  onClose,
  onOpenQuote,
  onViewAccount,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const [form, setForm] = useState<Form>(EMPTY_FORM);
  const [debounced, setDebounced] = useState<Form>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset everything every time the modal reopens.
  useEffect(() => {
    if (open) {
      setForm(EMPTY_FORM);
      setDebounced(EMPTY_FORM);
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Debounce 250 ms so we don't hammer the API on every keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(form), 250);
    return () => window.clearTimeout(t);
  }, [form]);

  // Name fields concatenate for search; user can type either first or
  // last alone, or "First Last" combined.
  const searchName = useMemo(
    () => `${debounced.firstName} ${debounced.lastName}`.trim(),
    [debounced.firstName, debounced.lastName]
  );
  const hasAnySearch = Boolean(
    searchName ||
      debounced.email.trim() ||
      debounced.phone.trim() ||
      debounced.postcode.trim()
  );

  const results = useQuery<AccountSearchResult[]>({
    queryKey: [
      "accounts-search",
      searchName,
      debounced.email.trim(),
      debounced.phone.trim(),
      debounced.postcode.trim(),
    ],
    queryFn: () =>
      searchAccounts({
        name: searchName,
        email: debounced.email,
        phone: debounced.phone,
        postcode: debounced.postcode,
      }),
    enabled: open && hasAnySearch,
    staleTime: 30_000,
  });

  async function handlePick(r: AccountSearchResult): Promise<void> {
    if (r.primaryCid === null || r.primaryLid === null) {
      setError(
        `Account #${r.aid} has no primary contact or location — open the existing card instead of creating a new quote here.`
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const visit = await createAccountVisit(r.aid, {
        cid: r.primaryCid,
        lid: r.primaryLid,
      });
      // Board cache is invalidated by the card.updated SSE the server
      // broadcasts. Cluster cache keyed by cardId — nuke the one that
      // matches so the Visits tab is fresh when staff opens it.
      if (r.cardId !== null) {
        await qc.invalidateQueries({ queryKey: ["cluster", r.cardId] });
      }
      onOpenQuote(visit.newVid);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "create failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleCreateNew(): Promise<void> {
    // Require at least one contact hook so we're not creating empty
    // shell records.
    const hasAny =
      form.firstName.trim() ||
      form.lastName.trim() ||
      form.email.trim() ||
      form.phone.trim();
    if (!hasAny) {
      setError(
        "Enter at least a name, email, or phone number before creating a new account."
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Match the convention used by CreateAccountModal — blank first name
      // coerces to "*" so templates render "Hi there" until staff fills
      // it in on the quote.
      const firstName = form.firstName.trim() || "*";
      const account = await createNewAccount({
        firstName,
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        // No cardId — top-bar flow has no parent card to re-link.
      });
      const visit = await createAccountVisit(account.aid, {
        cid: account.cid,
        lid: account.lid,
      });
      onOpenQuote(visit.newVid);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "create failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AnimatePresence>
      {open ? (
        <>
          <motion.div
            key="scrim"
            className="fixed inset-0 z-[80] bg-ink-950/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            key="search"
            className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto p-3 md:p-8"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
          >
            <div
              className="relative w-full max-w-3xl rounded-xl border border-ink-700 bg-ink-800 shadow-drag"
              onClick={(e) => e.stopPropagation()}
            >
              <header className="flex items-center justify-between border-b border-ink-700 px-5 py-4">
                <div>
                  <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-ink-200">
                    Search
                  </div>
                  <h3 className="mt-0.5 text-xl font-semibold text-ink-50">
                    Find a customer, then open their account or start a new quote
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  className="rounded-md border border-ink-500 px-3 py-1 text-[13px] text-ink-50 hover:border-ink-400"
                >
                  Close
                </button>
              </header>

              <div className="grid grid-cols-2 gap-3 px-5 pt-4 md:grid-cols-5">
                <LabeledInput
                  label="First name"
                  value={form.firstName}
                  onChange={(v) => setForm((f) => ({ ...f, firstName: v }))}
                  autoFocus
                />
                <LabeledInput
                  label="Last name"
                  value={form.lastName}
                  onChange={(v) => setForm((f) => ({ ...f, lastName: v }))}
                />
                <LabeledInput
                  label="Postcode"
                  value={form.postcode}
                  onChange={(v) => setForm((f) => ({ ...f, postcode: v }))}
                />
                <LabeledInput
                  label="Email"
                  value={form.email}
                  onChange={(v) => setForm((f) => ({ ...f, email: v }))}
                />
                <LabeledInput
                  label="Phone"
                  value={form.phone}
                  onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
                />
              </div>

              <div className="px-5 py-4">
                {!hasAnySearch ? (
                  <EmptyState>
                    Start typing a name, postcode, email or phone. Existing
                    customers will appear here — pick one to start a new quote
                    on their account.
                  </EmptyState>
                ) : results.isLoading ? (
                  <EmptyState>Searching…</EmptyState>
                ) : results.isError ? (
                  <EmptyState tone="error">
                    Search failed: {(results.error as Error).message}
                  </EmptyState>
                ) : (results.data ?? []).length === 0 ? (
                  <EmptyState>
                    No matches. Use <b>Create new account</b> below to start a
                    quote for a brand-new customer.
                  </EmptyState>
                ) : (
                  <ul className="divide-y divide-ink-700 rounded-md border border-ink-700 bg-ink-900/40">
                    {(results.data ?? []).map((r) => (
                      <ResultRow
                        key={r.aid}
                        result={r}
                        disabled={submitting}
                        onNewQuote={() => void handlePick(r)}
                        onViewAccount={() => onViewAccount(r.aid, r.cardId)}
                      />
                    ))}
                  </ul>
                )}
              </div>

              <footer className="flex items-center justify-between gap-2 border-t border-ink-700 px-5 py-3">
                <div className="text-[12px]">
                  {error ? (
                    <span className="text-slack-pink">⚠ {error}</span>
                  ) : (
                    <span className="text-ink-200">
                      Nothing above matches?
                    </span>
                  )}
                </div>
                <button
                  onClick={() => void handleCreateNew()}
                  disabled={submitting}
                  className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-3 py-1.5 text-[13px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? "Creating…" : "Create new account"}
                </button>
              </footer>
            </div>
          </motion.div>
        </>
      ) : null}
    </AnimatePresence>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoFocus?: boolean;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] uppercase tracking-[0.14em] text-ink-200">
        {label}
      </span>
      <input
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-ink-500 bg-ink-900 px-2 py-1.5 text-[13px] text-ink-50 placeholder:text-ink-300 focus:border-slack-aubergineLight focus:outline-none"
      />
    </label>
  );
}

function ResultRow({
  result: r,
  disabled,
  onNewQuote,
  onViewAccount,
}: {
  result: AccountSearchResult;
  disabled: boolean;
  onNewQuote: () => void;
  // Always wired — opens the existing card if r.cardId is set, else the
  // AccountSlideout preview keyed by aid.
  onViewAccount: () => void;
}): JSX.Element {
  return (
    <li className="flex items-center gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span
            className={`truncate text-[14px] font-medium ${
              r.deleted ? "text-ink-200 line-through" : "text-ink-50"
            }`}
          >
            {r.contactName || `Account #${r.aid}`}
          </span>
          {r.matchedFields.map((f) => (
            <MatchBadge key={f} field={f} />
          ))}
          {r.deleted ? (
            <span className="rounded border border-slack-pink/50 bg-slack-pink/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slack-pink">
              deleted
            </span>
          ) : null}
          {r.cardId !== null ? (
            <span className="rounded border border-ink-500 bg-ink-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ink-200">
              card #{r.cardId}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 truncate text-[12px] text-ink-200">
          {[r.postcode, r.email].filter(Boolean).map((piece, i, arr) => (
            <span key={`${piece}-${i}`}>
              {piece}
              {i < arr.length - 1 ? <span className="ml-2 text-ink-300">·</span> : null}
            </span>
          ))}
          {r.phone ? (
            <>
              {(r.postcode || r.email) ? (
                <span className="text-ink-300">·</span>
              ) : null}
              <PhoneLink number={r.phone} />
            </>
          ) : null}
          {fmtLastVisit(r) ? (
            <>
              {(r.postcode || r.email || r.phone) ? (
                <span className="text-ink-300">·</span>
              ) : null}
              <span>{fmtLastVisit(r)}</span>
            </>
          ) : null}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <button
          onClick={onViewAccount}
          disabled={disabled}
          className="rounded-md border border-ink-500 bg-ink-800 px-2.5 py-1 text-[12px] font-medium text-ink-50 hover:border-ink-400 hover:bg-ink-700 disabled:cursor-not-allowed disabled:opacity-50"
          title={
            r.cardId !== null
              ? "Open this customer's card"
              : "Create a card for this customer + open it (cluster-deduped by email)"
          }
        >
          Open card
        </button>
        <button
          onClick={onNewQuote}
          disabled={disabled}
          className="rounded-md border border-slack-aubergineLight bg-slack-aubergine px-2.5 py-1 text-[12px] font-medium text-ink-50 hover:bg-slack-aubergineLight disabled:cursor-not-allowed disabled:opacity-50"
          title="Start a fresh quote on this existing account"
        >
          + New quote
        </button>
      </div>
    </li>
  );
}

function MatchBadge({
  field,
}: {
  field: AccountSearchMatchField;
}): JSX.Element {
  const label = field.charAt(0).toUpperCase() + field.slice(1);
  return (
    <span className="rounded bg-slack-aubergine/40 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-ink-50">
      {label}
    </span>
  );
}

function fmtLastVisit(r: AccountSearchResult): string {
  if (r.visitCount === 0) return "no visits";
  if (!r.lastVisitDate) return `${r.visitCount} visit${r.visitCount === 1 ? "" : "s"}`;
  return `${r.visitCount} visit${r.visitCount === 1 ? "" : "s"}, last ${r.lastVisitDate}`;
}

function EmptyState({
  children,
  tone = "default",
}: {
  children: React.ReactNode;
  tone?: "default" | "error";
}): JSX.Element {
  const cls =
    tone === "error"
      ? "border-slack-pink/40 text-slack-pink"
      : "border-dashed border-ink-500 text-ink-200";
  return (
    <div
      className={`rounded-md border bg-ink-900/40 px-4 py-6 text-center text-[13px] ${cls}`}
    >
      {children}
    </div>
  );
}
